import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@fiverr-private/button';
import {
    Form,
    FormLabel,
    FormHeader,
    FormControl,
    FormElement,
    RadioGroup,
    Radio,
    SubmitHandler,
    z,
    zodResolver,
} from '@fiverr-private/forms';
import { Heading, Text, Typography } from '@fiverr-private/typography';
import { ModalBody, ModalCloseBtn, ModalHeader, ModalFooter, ModalContent } from '@fiverr-private/overlay';
import { Stack, Divider, Container } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Answers, ClassificationForm, ClassificationQuestionIds } from '../ComplianceModal/types';
import { reportEventsApi } from '../../biEvents';
import { ComplianceContext } from '../ComplianceProvider';

interface YesNoQuestionProps {
    labelKey: string;
    index: number;
}

const TRANSLATION_KEY = 'compliance.questioner';
const resolveTranslationKey = (key: string) => `${TRANSLATION_KEY}.${key}`;

const YesNoQuestion: React.FC<YesNoQuestionProps> = ({ labelKey, index }) => (
    <FormControl name={labelKey}>
        <FormHeader>
            <FormLabel>
                <Heading size={'h_xxs'} paddingBottom={'1'}>
                    {`${index + 1}. `}
                    <I18n k={resolveTranslationKey(`questions.${labelKey}`)} />
                </Heading>
            </FormLabel>
        </FormHeader>
        <FormElement>
            <RadioGroup>
                <Radio value="yes" name="yes">
                    <Typography color={'grey_1000'}>
                        <I18n k={resolveTranslationKey('yes')} />
                    </Typography>
                </Radio>
                <Radio value="no" name="no">
                    <Typography color={'grey_1000'}>
                        <I18n k={resolveTranslationKey('no')} />
                    </Typography>
                </Radio>
            </RadioGroup>
        </FormElement>
    </FormControl>
);

const questions = [
    ClassificationQuestionIds.DIRECTLY_MANAGE,
    ClassificationQuestionIds.SUBSTITUTE_ANOTHER,
    ClassificationQuestionIds.WORK_RESTRICTED,
    ClassificationQuestionIds.SAME_ASSIGNMENT_REQUIRES,
];

export const ClassificationQuestion = ({
    onSubmit,
    onClose,
    defaultValues,
    sellerId,
    isOpen,
}: {
    defaultValues?: ClassificationForm;
    onClose: () => void;
    onSubmit: SubmitHandler<ClassificationForm>;
    sellerId: number;
    isOpen: boolean;
}) => {
    const complianceContext = useContext(ComplianceContext);
    const Answer = [Answers.YES, Answers.NO] as const;
    type Answer = (typeof Answer)[number];
    const AnswerTypeSchema = z.enum(Answer);
    const schema = z.object({
        [ClassificationQuestionIds.DIRECTLY_MANAGE]: AnswerTypeSchema,
        [ClassificationQuestionIds.SUBSTITUTE_ANOTHER]: AnswerTypeSchema,
        [ClassificationQuestionIds.WORK_RESTRICTED]: AnswerTypeSchema,
        [ClassificationQuestionIds.SAME_ASSIGNMENT_REQUIRES]: AnswerTypeSchema,
    });
    type SchemaType = z.infer<typeof schema>;
    const methods = useForm<SchemaType>({
        defaultValues,
        resolver: zodResolver(schema),
    });

    const onSubmitData: SubmitHandler<SchemaType> = (data) => {
        onSubmit(data);
    };

    const handleAnimationEnd = () => {
        if (isOpen) {
            reportEventsApi.onClassificationQuestionView({
                pageName: complianceContext?.pageName ?? '',
                sellerId,
            });
        }
    };

    return (
        <ModalContent onAnimationEnd={handleAnimationEnd}>
            <ModalHeader>
                <I18n k={resolveTranslationKey('title')} />
                <ModalCloseBtn onClick={onClose} />
            </ModalHeader>
            <Form methods={methods} onSubmit={onSubmitData} overflow="auto" style={{ gap: '0' }}>
                <ModalBody>
                    <Container overflow="auto" maxHeight={{ default: 'calc(100vh - 180px)', md: '100%' }}>
                        <Stack gap={'3'} direction={'column'}>
                            <Text paddingBottom={'5'}>
                                <I18n k={resolveTranslationKey('body')} />
                            </Text>
                        </Stack>
                        <Stack direction="column" gap="6">
                            {questions.map((key, index) => (
                                <YesNoQuestion key={key} labelKey={key} index={index} />
                            ))}
                        </Stack>
                    </Container>
                </ModalBody>
                <Divider marginTop="8" />
                <ModalFooter>
                    <Button onClick={onClose} variant={'ghost'}>
                        <I18n k={resolveTranslationKey('cancel')} />
                    </Button>
                    <Button type="submit">
                        <I18n k={resolveTranslationKey('continue')} />
                    </Button>
                </ModalFooter>
            </Form>
        </ModalContent>
    );
};
